import { useEffect, useRef, useState } from "react";
import { api, apiError, fileUrl } from "@/lib/api";
import { refreshBranding, Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Crosshair, Rocket, RotateCcw } from "lucide-react";

const SLOTS = [
  { key: "logo_url", label: "Official Lomond Logo", hint: "Used in the header, login, registration and portal. PNG/SVG/WebP." },
  { key: "hero_url", label: "Homepage Hero", hint: "Top hero image behind 'Build a Career in Plant Operation'." },
  { key: "apply_url", label: "Journey 01 – Apply", hint: "Registering interest online at home." },
  { key: "simulator_url", label: "Journey 02 – Assess", hint: "Candidates using the Tenstar simulators." },
  { key: "train_url", label: "Journey 03 – Train", hint: "Operator beside Lomond machine — supervised practical training." },
  { key: "qualify_url", label: "Journey 04 – Qualify", hint: "Group with red Trained Operator cards and instructor." },
  { key: "develop_url", label: "Journey 05 – Develop", hint: "Live-site excavation work as a Development Operator." },
  { key: "progress_url", label: "Journey 06 – Progress", hint: "Group with blue Competent Operator cards." },
  { key: "why_url", label: "Why Lomond Image", hint: "Low-angle hi-vis image beside the Why Lomond section." },
  { key: "academy_hero_url", label: "Academy Hero (internal)", hint: "Reserved for trainee/operator academy areas." },
];

function SlotCard({ slot, draftUrl, publishedUrl, focal, fit, onUpload, onFocal, onFit, uploading }) {
  const imgRef = useRef(null);
  const [file, setFile] = useState(null);
  const dirty = (draftUrl || null) !== (publishedUrl || null);

  const pickFocal = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    onFocal(slot.key, x, y);
  };

  const [fx, fy] = (focal || "50% 50%").split(" ").map((v) => parseInt(v));

  return (
    <section className="bg-white border border-steel rounded-md p-5 space-y-3" data-testid={`branding-slot-${slot.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm">{slot.label}</h2>
          <p className="text-xs text-slate-500 mt-0.5">{slot.hint}</p>
        </div>
        <Badge variant="outline" className={`font-mono text-[9px] uppercase tracking-widest shrink-0 ${dirty ? "text-amber-800 bg-amber-100 border-amber-300" : "text-emerald-800 bg-emerald-100 border-emerald-300"}`} data-testid={`status-${slot.key}`}>
          {dirty ? "Unpublished" : "Published"}
        </Badge>
      </div>

      <div className="relative bg-elevated border border-steel rounded-sm h-40 overflow-hidden group cursor-crosshair"
        ref={imgRef} onClick={draftUrl ? pickFocal : undefined} data-testid={`preview-wrap-${slot.key}`}>
        {draftUrl ? (
          <>
            <img src={fileUrl(draftUrl)} alt={slot.label}
              className={`w-full h-full ${fit === "contain" ? "object-contain p-2 bg-white" : "object-cover"}`}
              style={{ objectPosition: focal || "50% 50%" }}
              data-testid={`preview-${slot.key}`} />
            {fit !== "contain" && (
              <span className="absolute h-4 w-4 -ml-2 -mt-2 pointer-events-none" style={{ left: `${fx}%`, top: `${fy}%` }} data-testid={`focal-marker-${slot.key}`}>
                <Crosshair className="h-4 w-4 text-lomond drop-shadow-[0_0_2px_black]" />
              </span>
            )}
            <span className="absolute bottom-1.5 right-1.5 text-[9px] font-mono uppercase tracking-widest bg-black/60 text-white px-1.5 py-0.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
              Click to set focal point
            </span>
          </>
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-xs font-mono uppercase tracking-widest text-slate-400" data-testid={`empty-${slot.key}`}>No image uploaded</span>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="bg-white border-steel text-charcoal text-xs flex-1 min-w-40" data-testid={`file-${slot.key}`} />
        <Button onClick={() => { onUpload(slot.key, file); setFile(null); }} disabled={!file || uploading === slot.key}
          className="bg-lomond text-black font-bold hover:bg-[#E6B400] shrink-0" size="sm" data-testid={`upload-${slot.key}`}>
          <Upload className="h-3.5 w-3.5 mr-1" /> {uploading === slot.key ? "..." : "Upload"}
        </Button>
        <div className="flex gap-1 bg-elevated border border-steel rounded-sm p-0.5" data-testid={`fit-${slot.key}`}>
          {[["cover", "Fill"], ["contain", "Fit"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => onFit(slot.key, v)}
              className={`px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-widest ${(fit || "cover") === v ? "bg-lomond text-black font-bold" : "text-slate-500"}`}
              data-testid={`fit-${v}-${slot.key}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BrandingPage() {
  const [state, setState] = useState(null);
  const [uploading, setUploading] = useState("");
  const [publishing, setPublishing] = useState(false);

  const load = () => api.get("/admin/branding").then((r) => setState(r.data)).catch(() => toast.error("Could not load branding"));
  useEffect(() => { load(); }, []);

  if (!state) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="branding-loading" />;

  const upload = async (key, file) => {
    if (!file) return;
    setUploading(key);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/branding/${key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Draft updated — publish to make it live.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(""); }
  };

  const setFocal = async (key, x, y) => {
    try {
      await api.post(`/admin/branding/${key}/focal`, { x, y });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setFit = async (key, fit) => {
    try {
      await api.post(`/admin/branding/${key}/fit`, { fit });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post("/admin/branding/publish");
      const { data } = await api.get("/branding");
      refreshBranding(data);
      toast.success("Branding published — live across the site.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setPublishing(false); }
  };

  const discard = async () => {
    if (!window.confirm("Discard all unpublished branding changes?")) return;
    try {
      await api.post("/admin/branding/discard");
      toast.success("Draft discarded.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="branding-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Admin</p>
          <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="branding-heading">Site Content &amp; Branding</h1>
          <p className="text-sm text-slate-500 mt-1">
            Uploads and focal points are saved as a draft. Publish when you're happy — the live site updates instantly.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={discard} disabled={!state.dirty} className="border-steel text-charcoal bg-white" data-testid="discard-draft-btn">
            <RotateCcw className="h-4 w-4 mr-1.5" /> Discard Draft
          </Button>
          <Button onClick={publish} disabled={!state.dirty || publishing} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="publish-branding-btn">
            <Rocket className="h-4 w-4 mr-1.5" /> {publishing ? "Publishing..." : "Publish Changes"}
          </Button>
        </div>
      </div>

      {state.dirty && (
        <p className="bg-amber-100 border border-amber-300 text-amber-900 text-sm rounded-md px-4 py-3" data-testid="draft-notice">
          You have unpublished changes. They are not visible on the live site until you publish.
        </p>
      )}

      <section className="bg-white border border-steel rounded-md p-6" data-testid="logo-preview-section">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Live header preview</p>
        <div className="bg-elevated border border-steel rounded-sm p-6 inline-block">
          <Logo />
        </div>
      </section>

      <div className="grid sm:grid-cols-2 gap-4">
        {SLOTS.map((slot) => (
          <SlotCard key={slot.key} slot={slot}
            draftUrl={state.draft?.[slot.key]}
            publishedUrl={state.published?.[slot.key]}
            focal={state.draft_focal?.[slot.key]}
            fit={state.draft_fit?.[slot.key]}
            onUpload={upload} onFocal={setFocal} onFit={setFit} uploading={uploading} />
        ))}
      </div>
    </div>
  );
}
